import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { NoEvaluationNotice } from 'src/components/ui/no-evaluation-notice.tsx';
import { SECTIONS } from 'src/data/sections.ts';
import apiClient from 'src/lib/apiClient.ts';
import { getResultType, shortSectionTitle, truncateToDisplay } from 'src/lib/scoring.ts';

import { ResultPageContent } from './result-page-content.tsx';

interface PersistedResult {
  total_score: number;
  result_id: number;
  answers: Record<string, number>;
  context: { role?: string; size?: string; type?: string };
  section_scores: { id: string; title: string; score: number }[];
}

export function FetchedResultPage() {
  const [params] = useSearchParams();
  const id = params.get('id') ?? '';
  const navigate = useNavigate();

  const { data, isPending, isError } = useQuery<PersistedResult>({
    queryKey: ['result', id],
    queryFn: () => apiClient.get<PersistedResult>(`/results/${id}`).then((r) => r.data),
    enabled: !!id,
    retry: 1
  });

  if (!id) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-body-lg text-zeb-gray-600">Keine Ergebnis-ID angegeben.</p>
        <button onClick={() => navigate('/')} className="text-sm text-zeb-primary hover:underline">
          Zum Start
        </button>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-zeb-muted border-t-zeb-primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center px-5">
        <p className="text-body-lg text-zeb-gray-600">
          Ergebnis konnte nicht geladen werden. Bitte überprüfen Sie die ID und versuchen Sie es
          erneut.
        </p>
        <button onClick={() => navigate('/')} className="text-sm text-zeb-primary hover:underline">
          Neuen Check starten
        </button>
      </div>
    );
  }

  // total_score <= 0 ist das Sentinel für „keine substanziellen Antworten"
  // (Skala läuft 1–5) → keine Einstufung statt „Customer-Blind" mit 0,0.
  if (data.total_score <= 0) {
    return (
      <NoEvaluationNotice
        onRestart={() => navigate('/')}
        message="Dieses Ergebnis enthält keine bewerteten Fragen, daher ist keine Reifegrad-Einstufung möglich."
      />
    );
  }

  const overall = truncateToDisplay(data.total_score);
  const resultType = getResultType(overall);

  const levels = data.section_scores.map((ss) => {
    const section = SECTIONS.find((s) => s.id === ss.id);
    // 0-Sentinel → „keine Angabe" (siehe result-page.tsx resultPayload).
    const score = ss.score > 0 ? truncateToDisplay(ss.score) : undefined;
    return {
      id: ss.id as 'strategie' | 'loop' | 'wirkungsebenen',
      name: section ? shortSectionTitle(section) : ss.title,
      score,
      tone: getResultType(score ?? 1).tone,
      Icon: section?.icon ?? SECTIONS[0].icon
    };
  });

  return (
    <ResultPageContent
      overall={overall}
      resultType={resultType}
      levels={levels}
      lead={{ name: '', company: '', email: '' }}
      answers={data.answers}
      context={data.context}
      onReset={() => navigate('/')}
    />
  );
}
